// Back, on the routing history, capped at the Dashboard (named "Inbox" when it shows the WhatsApp inbox) (Antonin, 13 Oct, both apps; the practitioner app's navStack.ts).
// One small stack of the top-level pages visited in this tab, keyed by section ('/', '/patients', '/billing'…):
//   - opening a page pushes it (its latest address is kept, so Back returns to the same view, filters included);
//   - reaching the Dashboard resets the stack (the cap: Back never walks past home);
//   - opening a page already in the stack cuts the stack back to it, so Back can never loop between siblings;
//   - Back goes to the page before, or to the Dashboard when nothing is left.
// The browser's own Back still walks the full browser history.
import { useEffect, useSyncExternalStore } from 'react';
import { dashboardNavLabel } from '@/app/lib/dashboard-nav-label';

export const HOME = '/dashboard';
type Entry = { key: string; to: string; label: string };
let stack: Entry[] = [];
const subs = new Set<() => void>();
const emit = () => subs.forEach((f) => f());

export function visit(e: Entry): void {
  if (e.key === HOME) stack = [e];
  else {
    const i = stack.findIndex((x) => x.key === e.key);
    stack = i >= 0 ? [...stack.slice(0, i), e] : [...stack, e];
  }
  emit();
}

/** Where Back goes from the current page: the page before it, or the Dashboard. Null on the Dashboard. */
export function backTarget(key: string): Entry | null {
  if (key === HOME) return null;
  const i = stack.findIndex((x) => x.key === key);
  return (i > 0 ? stack[i - 1] : null) ?? { key: HOME, to: HOME, label: dashboardNavLabel() };
}

export function useNavStack(entry: Entry | null): Entry | null {
  useEffect(() => { if (entry) visit(entry); }, [entry?.key, entry?.to]); // eslint-disable-line react-hooks/exhaustive-deps
  useSyncExternalStore((f) => { subs.add(f); return () => { subs.delete(f); }; }, () => stack, () => stack);
  return entry ? backTarget(entry.key) : null;
}
